import { observer } from 'mobx-react';
import { useEffect } from 'react';

import { useStore } from '@roc/client-portal-shared/hooks';
import { useUserStore } from '@roc/feature-app-core';
import { BorrowerLoanFastTrackPaymentDialog, LOAN_FASTRACK } from '@roc/feature-loan-fasttrack';
import { useLoanStore } from '@roc/feature-loans';
import {
  ACH_FORM,
  APPRAISAL_DETAILS,
  BORROWER_BACKGROUND_REVIEW,
  BORROWER_CREDIT_REVIEW,
  BORROWER_ENTITY,
  BORROWER_SETUP,
  BORROWER_IDENTITY_VERIFICATION,
  LOAN_FAST_TRACK_PAYMENT,
  CREDIT_AUTHORIZATION,
  RESOLUTION_ITEM_FORM,
  SCOPE_OF_WORK_FORM_V2,
  SCOPE_OF_WORK_LINK_V2,
  TRACK_RECORD,
  TRACK_RECORD_REVIEW,
  INSURANCE_INFORMATION,
  VALUATION_PAYMENT,
  TRACK_RECORD_RESOLUTION_ITEM_FORM,
  BORROWER_LIQUIDITY,
  DOC_REVIEW_FORM,
  LEASE_INFORMATION_UNIT_PREFIX,
  PERMITS,
  PURCHASE_CONTRACT,
} from '../../constants';
import { useDocumentStore } from '../../hooks/useDocumentStore';
import { BorrowerBackgroundReviewDialog } from './borrowerBackgroundReview/borrowerBackgroundReviewDialog';
import { NewBorrowerBaseballCardDialog } from './borrowerBaseballCard/newBorrowerBaseballCardDialog';
import { TrackRecordLineItemDialog } from './borrowerBaseballCard/trackRecordLineItemDialog';
import { BorrowerCreditReviewDialog } from './borrowerCreditReview/borrowerCreditReviewDialog';
import BorrowerEntityDialog from './borrowerEntity/borrowerEntityDialog';
import ResolutionItem from './resolutionItem/resolutionItem';
import { AchConsentFormDialog } from '@roc/feature-plaid';
import { TamariskAppraisalOrderPaymentContainerDialog } from '@roc/feature-tamarisk-payments';
import { BorrowerScopeOfWorkV2LinkDialog, ScopeOfWorkV2FormDialog } from '@roc/feature-sow-v2';
import { BorrowerSetupToDoDialog, IdAndFinancialInformationDialog, PersonalAndCreditInformationDialog } from '@roc/feature-borrowers';
import TrackRecordResolutionItem from './resolutionItem/trackRecordResolutionItem';
import { BorrowerLoanApplicationsStore } from '@roc/feature-borrower-loans';
import { InsuranceInformationDialog } from '@roc/feature-insurance-review';
import { AppraisalPaymentLinkModal } from './appraisalDetailsV2/components/appraisalDetails';
import { BorrowerLiquidityDialog } from '@roc/feature-bank-statements';
import { DocReviewDialogV2, PurchaseContractReviewDialog } from '@roc/feature-ai-docs-review';
import { PermitsDialog } from './permits/permitsDialog';
import { LeaseUnitModal } from '@roc/feature-leases';

export type BorrowerDocumentFormsProps = {
  loanId: string;
  isLoanApplication: boolean;
  borrowerLoanApplicationsStore: BorrowerLoanApplicationsStore
};

export const BorrowerDocumentForms = observer((props: BorrowerDocumentFormsProps) => {
  const { loanId, isLoanApplication, borrowerLoanApplicationsStore } = props;
  const { loanStore } = useLoanStore();
  const {
    documentStore: { documentFormStore, currentDocument },
  } = useDocumentStore();
  const { globalStore, insuranceInformationStore, appraisalDetailsV2Store } = useStore();
  const loanDetails = isLoanApplication ? borrowerLoanApplicationsStore.loanDetails : loanStore.loanDetails;

  useEffect(() => {
    if (!loanDetails || loanDetails.loanId !== Number(loanId)) {
      isLoanApplication ? borrowerLoanApplicationsStore.fetchLoanApplicationDetails(loanId) : loanStore.fetchLoanDetails(loanId);
    }
  }, [loanId, isLoanApplication]);

  const refreshLoan = () => {
    isLoanApplication ? borrowerLoanApplicationsStore.fetchLoanApplicationDetails(loanId) : loanStore.refreshLoanDetails();
  }

  const { documentStore } = useDocumentStore();
  const closeCurrentForm = () => {
    documentFormStore.closeCurrentForm();
  };

  const closeNoteAllongeAndAssignment = () => {
    documentFormStore.onCloseNoteAllongeAndAssignment();
  }

  const plaidShareBaseURL = window.location.href.split('lender-portal');
  const { userStore } = useUserStore();
  const allowLoanPricing = userStore.allowLoanPricing;
  const currentForm = documentFormStore.currentForm;
  const isClosingConditionForm = currentForm && currentForm.includes('Closing Condition - ');

  useEffect(() => {
    return () => {
      documentFormStore.reset();
    };
  }, [documentFormStore]);

  console.log('loanStore', loanStore);
  console.log('currentDocument', currentDocument);
  return (
    <>
      <BorrowerBackgroundReviewDialog
        open={documentFormStore.currentForm === BORROWER_BACKGROUND_REVIEW}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <BorrowerCreditReviewDialog
        open={documentFormStore.currentForm === BORROWER_CREDIT_REVIEW}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <NewBorrowerBaseballCardDialog
        open={documentFormStore.currentForm === TRACK_RECORD_REVIEW}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <TrackRecordLineItemDialog
        open={documentFormStore.currentForm === TRACK_RECORD}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <ResolutionItem
        open={documentFormStore.currentForm === RESOLUTION_ITEM_FORM}
        onClose={closeCurrentForm}
        loanTaskId={currentDocument?.taskId}
      />
      <BorrowerEntityDialog
        open={documentFormStore.currentForm === BORROWER_ENTITY}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <BorrowerLoanFastTrackPaymentDialog
        open={documentFormStore.currentForm === LOAN_FAST_TRACK_PAYMENT}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <AchConsentFormDialog
        open={documentFormStore.currentForm === ACH_FORM}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <TamariskAppraisalOrderPaymentContainerDialog
        open={documentFormStore.currentForm === APPRAISAL_DETAILS}
        onClose={closeCurrentForm}
        loanId={loanId}
        loanDetails={loanDetails}
        document={currentDocument}
      />
      <BorrowerScopeOfWorkV2LinkDialog
        open={documentFormStore.currentForm === SCOPE_OF_WORK_LINK_V2}
        onClose={closeCurrentForm}
        loanId={currentDocument?.loanId}
        document={currentDocument}
      />
      <ScopeOfWorkV2FormDialog
        open={documentFormStore.currentForm === SCOPE_OF_WORK_FORM_V2}
        onClose={closeCurrentForm}
        store={documentFormStore.scopeOfWorkV2FormStore}
      />
      <BorrowerSetupToDoDialog
        open={documentFormStore.currentForm === BORROWER_SETUP}
        borrowerId={currentDocument?.borrowerId}
        loan={loanDetails}
        handleClose={closeCurrentForm}
        refreshLoan={() => {
          refreshLoan();
        }}
        document={null}
      />
      <PersonalAndCreditInformationDialog
        open={documentFormStore.currentForm === CREDIT_AUTHORIZATION}
        borrowerId={currentDocument?.borrowerId}
        loan={loanDetails}
        handleClose={closeCurrentForm}
        refreshLoan={() => {
          refreshLoan();
        }}
        document={null}
      />
      <IdAndFinancialInformationDialog
        open={documentFormStore.currentForm === BORROWER_IDENTITY_VERIFICATION}
        borrowerId={currentDocument?.borrowerId}
        loan={loanDetails}
        handleClose={closeCurrentForm}
        refreshLoan={() => {
          refreshLoan();
        }}
        document={null}
      />
      <InsuranceInformationDialog
        open={documentFormStore.currentForm === INSURANCE_INFORMATION}
        onClose={closeCurrentForm}
        document={currentDocument}
        store={insuranceInformationStore}
        loanId={loanId}
      />
      <AppraisalPaymentLinkModal
        open={documentFormStore.currentForm === VALUATION_PAYMENT}
        onClose={closeCurrentForm}
        store={appraisalDetailsV2Store}
        loanId={loanStore.loanDetails?.loanId}
        document={currentDocument}
      />
      <TrackRecordResolutionItem
        open={
          documentFormStore.currentForm === TRACK_RECORD_RESOLUTION_ITEM_FORM
        }
        onClose={closeCurrentForm}
        loanTaskId={currentDocument?.taskId}
        document={currentDocument}
      />
      <BorrowerLiquidityDialog
        open={documentFormStore.currentForm === BORROWER_LIQUIDITY}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <DocReviewDialogV2
        open={documentFormStore.currentForm === DOC_REVIEW_FORM}
        handleClose={closeCurrentForm}
        taskId={currentDocument?.taskId}
      />
      <LeaseUnitModal
        open={!!documentFormStore.currentForm?.startsWith(LEASE_INFORMATION_UNIT_PREFIX)}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <PurchaseContractReviewDialog
        open={documentFormStore.currentForm === PURCHASE_CONTRACT}
        handleClose={closeCurrentForm}
        taskId={currentDocument?.taskId}
      />
      <PermitsDialog
        open={documentFormStore.currentForm === PERMITS}
        onClose={closeCurrentForm}
        loan={loanDetails}
        document={currentDocument}
      />
      {/* Add additional forms in here */}
    </>
  );
});
